// Custom hook for form validation with React Hook Form and Zod.
// Sanitization is handled by Zod schema transforms (see zod-sanitizers.ts),
// NOT by a blanket post-parse pass.
import { useForm, UseFormProps, FieldValues, UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useCallback } from 'react';
import { createLogger } from '@/lib/structuredLogger';

const logger = createLogger('useFormValidation');

interface UseFormValidationOptions<T extends FieldValues> extends Omit<
  UseFormProps<T>,
  'resolver'
> {
  schema: z.ZodType<T>;
}

interface UseFormValidationReturn<T extends FieldValues> extends UseFormReturn<T> {
  sanitizedSubmit: (
    onValid: (data: T) => void | Promise<void>,
  ) => (e?: React.BaseSyntheticEvent) => Promise<void>;
}

/**
 * Custom hook that combines React Hook Form with Zod validation.
 *
 * @example
 * ```tsx
 * const { register, sanitizedSubmit, formState: { errors } } = useFormValidation({
 *   schema: loginSchema,
 *   mode: 'onChange',
 * });
 *
 * <form onSubmit={sanitizedSubmit(async (data) => {
 *   await handleLogin(data);
 * })}>
 * ```
 */
export const useFormValidation = <T extends FieldValues>({
  schema,
  ...options
}: UseFormValidationOptions<T>): UseFormValidationReturn<T> => {
  const form = useForm<T>({
    ...options,
    resolver: zodResolver(schema),
  });

  const sanitizedSubmit = useCallback(
    (onValid: (data: T) => void | Promise<void>) => {
      return form.handleSubmit(async (data) => {
        try {
          await onValid(data);
        } catch (error) {
          logger.error(
            'Form submission failed',
            error instanceof Error ? error : new Error(String(error)),
          );
          throw error;
        }
      });
    },
    [form],
  );

  return {
    ...form,
    sanitizedSubmit,
  };
};

/**
 * Helper to extract error messages from form state
 */
export const getFormErrorMessages = (errors: Record<string, unknown>): Record<string, string> => {
  const messages: Record<string, string> = {};

  Object.entries(errors).forEach(([key, value]) => {
    if (value && typeof value === 'object' && 'message' in value) {
      messages[key] = value.message as string;
    }
  });

  return messages;
};
